<script>
import {
  iIcon,
  iModal,
  iSearchInput,
  iButton,
  iTabs,
  iTab,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iSpinner,
  iTabsOrientation,
  iTabsSize,
  iTabsContainerTypes,
  closeModal,
  iBadge,
  iFeaturedIcon,
  iFeaturedIconSizes,
} from '@inscribe/signal';
import CustomerSearchResult from '@/V2/components/global-search/CustomerSearchResult.vue';
import DocumentSearchResult from '@/V2/components/global-search/DocumentSearchResult.vue';
import LoadingItem from '@/V2/components/global-search/LoadingItem.vue';
import palette from '@inscribe/signal/dist/palette';
import { mapGetters, mapActions } from 'vuex';

const TAB_INDEX = {
  ALL: 0,
  CUSTOMERS: 1,
  DOCUMENTS: 2,
};

const DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB = 3;
const STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB = 6;
const MAX_RESULTS_TO_SHOW_IN_OTHER_TABS = 10;

const DEBOUNCE_TIME_MS = 300;

export default {
  components: {
    iIcon,
    iModal,
    iSearchInput,
    iButton,
    iTabs,
    iTab,
    iBadge,
    iFeaturedIcon,
    iSpinner,
    CustomerSearchResult,
    DocumentSearchResult,
    LoadingItem,
  },
  data() {
    return {
      searchQuery: '',
      searchQueryUuid: null,
      selectedTabIndex: 0,
      error: '',
      palette,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iTabsOrientation,
      iTabsSize,
      iTabsContainerTypes,
      closeModal,
      iFeaturedIconSizes,
      TAB_INDEX,
      DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB,
      STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB,
      MAX_RESULTS_TO_SHOW_IN_OTHER_TABS,
      DEBOUNCE_TIME_MS,
    };
  },
  watch: {
    searchQuery: {
      handler(searchQueryValue) {
        // If the user is making a natural language search, we'll wait to
        // fetch results until they press Enter.
        if (this.isNaturalLanguageQuery) {
          return;
        }
        // Even though we may have a full result set cached,
        // this could be stale so we re-fetch the data in the background
        this.fetchSearchResults(searchQueryValue);
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters([
      'getCachedSearchResults',
      'nameAndContentSearchResultsAreCachedForQuery',
      'isOrgAdvancedUserOrAdmin',
      'isFetchingNameOnlySearchResultsByQuery',
      'isFetchingNameAndContentSearchResultsByQuery',
    ]),
    numCustomerResultsToShow() {
      if (this.isAllTabSelected) {
        const noDocResults = this.searchResults?.data?.documents.length === 0;
        return noDocResults
          ? STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB
          : DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB;
      }
      return this.searchResults?.data?.customers.length;
    },
    numDocumentResultsToShow() {
      if (this.isAllTabSelected) {
        const noCustomerResults =
          this.searchResults?.data?.customers.length === 0;
        return noCustomerResults
          ? STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB
          : DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB;
      }
      return this.searchResults?.data?.documents.length;
    },
    isFetching() {
      return (
        this.isFetchingNameOnlySearchResultsByQuery(this.searchQuery) ||
        this.isFetchingNameAndContentSearchResultsByQuery(this.searchQuery)
      );
    },
    customerResultsForDisplay() {
      return this.searchResults?.data?.customers.slice(
        0,
        this.numCustomerResultsToShow,
      );
    },
    customerHeadingText() {
      if (this.isEmptySearchQuery) {
        return 'Recent Customers';
      }
      return 'Customers';
    },
    documentResultsForDisplay() {
      return this.searchResults?.data?.documents.slice(
        0,
        this.numDocumentResultsToShow,
      );
    },
    documentHeadingText() {
      if (this.isEmptySearchQuery) {
        return 'Recent Documents';
      }
      return 'Documents';
    },
    shouldShowNoMatchesFound() {
      if (this.isCustomersTabSelected) {
        return (
          this.isSearchingForInitialResults === false &&
          this.areCustomerResultsFound === false
        );
      }

      if (this.isSearchFullyComplete === false) return false;

      if (this.isAllTabSelected) {
        return (
          this.isSearchingForInitialResults === false &&
          !this.summaryForDisplay &&
          this.areCustomerResultsFound === false &&
          this.areDocumentResultsFound === false
        );
      }

      if (this.isDocumentsTabSelected) {
        return (
          this.isSearchingForInitialResults === false &&
          this.areDocumentResultsFound === false
        );
      }

      return false;
    },
    shouldShowTabs() {
      return (
        this.searchResults?.data?.documents?.length >
          DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB ||
        this.searchResults?.data?.customers?.length >
          DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB
      );
    },
    shouldShowCustomerResults() {
      return this.isAllOrCustomersTabSelected && this.areCustomerResultsFound;
    },
    shouldShowDocumentResults() {
      return this.isAllOrDocumentsTabSelected && this.areDocumentResultsFound;
    },
    shouldShowSearchingInsideDocumentsText() {
      if (this.isCustomersTabSelected) {
        return false;
      }

      let docResultsAreFullyPopulated = false;
      if (this.isAllTabSelected) {
        docResultsAreFullyPopulated =
          this.documentResultsForDisplay?.length >=
          this.DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB;
      }
      if (this.isDocumentsTabSelected) {
        docResultsAreFullyPopulated =
          this.documentResultsForDisplay?.length >=
          this.MAX_RESULTS_TO_SHOW_IN_OTHER_TABS;
      }

      if (
        this.isSearchingInDocumentContentsOnly &&
        !docResultsAreFullyPopulated
      ) {
        return true;
      }
      return false;
    },
    shouldShowViewMoreCustomersButton() {
      if (!this.isAllTabSelected) {
        return false;
      }
      const { customers, documents } = this.searchResults?.data ?? {};
      const numCustomerResultsToShow =
        documents?.length === 0
          ? STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB
          : DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB;
      return customers?.length > numCustomerResultsToShow;
    },
    shouldShowViewMoreDocumentsButton() {
      if (!this.isAllTabSelected) {
        return false;
      }
      const { customers, documents } = this.searchResults?.data ?? {};
      const numDocResultsToShow =
        customers?.length === 0
          ? STANDALONE_MAX_RESULTS_TO_SHOW_IN_ALL_TAB
          : DEFAULT_MAX_RESULTS_TO_SHOW_IN_ALL_TAB;
      return documents?.length > numDocResultsToShow;
    },
    isEmptySearchQuery() {
      // if the search query is empty, we are showing recent results
      return this.searchQuery.length === 0;
    },
    isSearchingForInitialResults() {
      return !this.searchResults;
    },
    isSearchFullyComplete() {
      return this.nameAndContentSearchResultsAreCachedForQuery(
        this.searchQuery,
      );
    },
    isSearchingInDocumentContentsOnly() {
      return (
        this.isSearchingForInitialResults === false &&
        this.isSearchFullyComplete === false
      );
    },
    searchResults() {
      return this.getCachedSearchResults(this.searchQuery);
    },
    areCustomerResultsFound() {
      return Boolean(this.customerResultsForDisplay?.length);
    },
    areDocumentResultsFound() {
      return Boolean(this.documentResultsForDisplay?.length);
    },
    isAllTabSelected() {
      return this.selectedTabIndex === this.TAB_INDEX.ALL;
    },
    isCustomersTabSelected() {
      return this.selectedTabIndex === this.TAB_INDEX.CUSTOMERS;
    },
    isDocumentsTabSelected() {
      return this.selectedTabIndex === this.TAB_INDEX.DOCUMENTS;
    },
    isAllOrCustomersTabSelected() {
      return this.isAllTabSelected || this.isCustomersTabSelected;
    },
    isAllOrDocumentsTabSelected() {
      return this.isAllTabSelected || this.isDocumentsTabSelected;
    },
  },
  methods: {
    ...mapActions([
      'fetchAndCacheNameOnlySearchResults',
      'fetchAndCacheFullContentSearchResults',
    ]),
    formatErrorMessage(error) {
      let message = 'There was an issue querying your data. Please try again.';
      if (error.response?.data?.message) {
        const raw = error.response?.data?.message;
        if (raw.startsWith('Internal server error. ')) {
          message = raw.substring('Internal server error. '.length);
        } else {
          message = raw;
        }
      }
      return message;
    },
    async fetchSearchResults(searchQuery) {
      // Generate UUID for current query so we can poll for processing events
      // using the same UUID
      this.searchQueryUuid = crypto.randomUUID();

      const promises = [
        this.fetchAndCacheNameOnlySearchResults({
          query: searchQuery,
          queryUuid: this.searchQueryUuid,
        }),
      ];
      if (searchQuery.length > 0) {
        // If it's not a natural language query, we also run a search on the
        // backend for document full-text content
        promises.push(
          this.fetchAndCacheFullContentSearchResults({
            query: searchQuery,
          }),
        );
      }
      try {
        this.error = '';
        await Promise.all(promises);
      } catch (error) {
        this.error = this.formatErrorMessage(error);
      }
    },
    onInputChanged(value) {
      this.searchQuery = value;
      // Clear the error when the user changes the search query
      this.error = '';
    },
    onOpen() {
      this.onTabSelected(this.TAB_INDEX.ALL);
      // When modal is opened, refetch search results in background. This covers
      // cases when new customers/documents have been added or review statuses
      // have changed since the last time the modal was opened.
      this.fetchSearchResults(this.searchQuery);
    },
    onClose() {
      closeModal('global-search-modal');
    },
    onTabSelected(tabIndex) {
      this.selectedTabIndex = tabIndex;
      this.scrollContentToTop();
    },
    scrollContentToTop() {
      const content = this.$refs.searchResults;
      if (content) {
        content.scrollTop = 0;
      }
    },
    handleKeydown(event) {
      // If the modal is not open, don't handle keydown events
      if (
        !document.querySelector(
          'div[data-modal-id="global-search-modal"].is-open',
        )
      ) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return closeModal('global-search-modal');
      }
    },
    focusSearchInput() {
      let searchInput = this.$refs.searchInput?.$el;
      if (searchInput) {
        let input = searchInput.querySelector('input');
        input.focus();
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
};
</script>

<template>
  <i-modal
    modal-id="global-search-modal"
    title="Search"
    :show-footer="false"
    :close-on-click-outside="true"
    :has-padding="false"
    :with-body="false"
    class="search-modal"
    @on-open="onOpen"
  >
    <template #header>
      <div class="header d-flex flex-column">
        <i-search-input
          class="i-py-9 i-px-10"
          size="large"
          input-placeholder="Search"
          :model-value="searchQuery"
          :keyboard-shortcut="false"
          :debounce-time="DEBOUNCE_TIME_MS"
          :loading-icon="
            searchQuery !== '' && this.isSearchingForInitialResults
          "
          data-dd-action-name="global-search-input"
          ref="searchInput"
          @update:model-value="onInputChanged"
        />
        <i-tabs
          v-if="shouldShowTabs"
          class="tabs i-py-5 i-px-9"
          :orientation="iTabsOrientation.HORIZONTAL"
          :size="iTabsSize.SMALL"
          :type="iTabsContainerTypes.BUTTONS"
          :preselected-tab="selectedTabIndex"
          @tab-selected="onTabSelected"
        >
          <template #tabs>
            <i-tab
              label="All"
              data-dd-action-name="global-search-all-tab"
            ></i-tab>
            <i-tab
              label="Customers"
              data-dd-action-name="global-search-customers-tab"
            ></i-tab>
            <i-tab
              label="Documents"
              data-dd-action-name="global-search-documents-tab"
            ></i-tab>
          </template>
        </i-tabs>
      </div>
    </template>

    <template #body-content>
      <div class="search-results" ref="searchResults">
        <div v-if="error" class="i-text-error-90 i-px-10 i-pb-10">
          {{ error }}
        </div>

        <div
          v-else-if="shouldShowNoMatchesFound"
          class="i-text-neutral-100 i-px-10 i-pb-10"
        >
          No matches
        </div>

        <div
          class="d-flex flex-column i-pb-10 i-px-7"
          v-else-if="isSearchingForInitialResults"
        >
          <!-- Show loading bars for basic queries -->
          <div class="d-flex flex-column i-gap-4 i-mt-6">
            <loading-item v-for="i in 3" :key="`customer-${i}`" />
          </div>
        </div>
        <div v-else class="d-flex flex-column">
          <div
            class="customer-results d-flex flex-column i-pb-10 i-px-7"
            v-if="shouldShowCustomerResults"
          >
            <h3 class="section-title i-px-5">{{ customerHeadingText }}</h3>
            <div class="results-items i-mt-2 align-items-start">
              <customer-search-result
                v-for="customer in customerResultsForDisplay"
                :key="customer.id"
                :customer="customer"
                v-highlight-text="{
                  subElementTargetSelector: '.name',
                  textToHighlight: searchQuery,
                }"
                @click="onClose"
              />
              <i-button
                v-if="shouldShowViewMoreCustomersButton"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.SOLID_LIGHT"
                :size="iButtonSizes.SMALL"
                label="View More"
                class="i-mt-4 i-ml-4"
                @button-click="onTabSelected(TAB_INDEX.CUSTOMERS)"
                data-dd-action-name="global-search-view-more-customers-button"
              />
            </div>
          </div>

          <div
            class="document-results d-flex flex-column i-px-7 i-pb-10"
            v-if="shouldShowDocumentResults"
          >
            <h3 class="section-title i-px-5">{{ documentHeadingText }}</h3>
            <div class="results-items i-mt-2 align-items-start">
              <document-search-result
                v-for="document in documentResultsForDisplay"
                :key="document.id"
                :document="document"
                :search-query="searchQuery"
                v-highlight-text="{
                  subElementTargetSelector: '.name',
                  textToHighlight: searchQuery,
                }"
                @click="onClose"
              />
              <i-button
                v-if="shouldShowViewMoreDocumentsButton"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.SOLID_LIGHT"
                :size="iButtonSizes.SMALL"
                label="View More"
                class="i-mt-4 i-ml-4"
                @button-click="onTabSelected(TAB_INDEX.DOCUMENTS)"
                data-dd-action-name="global-search-view-more-documents-button"
              />
            </div>
          </div>

          <div
            class="section-title i-ml-5 i-px-7 d-flex flex-row align-items-center i-gap-2"
            v-if="shouldShowSearchingInsideDocumentsText"
          >
            Searching inside documents...
            <i-spinner size="sm" type="neutral"></i-spinner>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <!-- empty footer -->
    </template>
  </i-modal>
</template>

<style lang="scss">
.i-modal.search-modal .modal-content.d-flex {
  width: 720px;
}
</style>

<style lang="scss" scoped>
.header {
  border-bottom: 1px solid $light-neutral-60;

  .i-search-input {
    height: 100%;
    border: 0;
    font-size: 1.5rem;
  }

  .tabs {
    border-top: 1px solid $light-neutral-60;
  }
}

.search-results {
  height: 480px;
  overflow-y: auto;
  margin-top: -24px;
  padding-top: 24px;

  .no-results {
    color: $light-neutral-100;
  }
}

.customer-results:not(:last-child) {
  margin-bottom: 24px;
}

.section-title {
  color: $light-neutral-100;
  font-size: 0.875rem;
  font-weight: 400;
}

.results-items {
  display: flex;
  flex-direction: column;
}
</style>
